<template>
    <section class="tow-col-section">
        <div class="tow-col-section__container">
            <div class="tow-col__card">
                <CardTitle v-if="!hideTitle" :module="item" />
                <div class="tow-col__grid">
                    <div class="tow-col__column">
                        <a v-for="(cell, idx) in leftCol"
                            :key="'l-' + String((cell as Record<string, unknown>)?.id ?? idx)" class="tow-col__link"
                            @click="handleCellClick(cell)">
                            {{ getCellTitle(cell) }}
                        </a>
                    </div>
                    <div class="tow-col__column">
                        <a v-for="(cell, idx) in rightCol"
                            :key="'r-' + String((cell as Record<string, unknown>)?.id ?? idx)" class="tow-col__link"
                            @click="handleCellClick(cell)">
                            {{ getCellTitle(cell) }}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Module } from '@/api/page_config'
import CardTitle from './CardTitle.vue'
import { getStrWithRecord, jumpToUrl } from './index.js'

const props = withDefaults(defineProps<{
    item: Module
    hideTitle?: boolean
}>(), { hideTitle: false })

const emit = defineEmits<{
    (e: 'click', item: Module, cell?: unknown): void
}>()

function getCellTitle(cell: unknown): string {
    return getStrWithRecord(cell, 'text')
}
function getCellUrl(cell: unknown): string {
    return getStrWithRecord(cell, 'url')
}

const list = computed(() => props.item.fieldData || [])

const leftCol = computed(() => {
    const arr = list.value
    const half = Math.ceil(arr.length / 2)
    return arr.slice(0, half)
})

const rightCol = computed(() => {
    const arr = list.value
    const half = Math.ceil(arr.length / 2)
    return arr.slice(half)
})

function handleCellClick(cell: unknown): void {
    emit('click', props.item, cell)
    const url = getCellUrl(cell)
    if (url) jumpToUrl(url)
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

$link-color: #2563eb;

.tow-col-section {
    width: 100%;
}

.tow-col-section__container {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0 1rem;

    @media (min-width: $breakpoint-md) {
        padding: 0 2rem;
    }
}

.tow-col__card {
    background: #ffffff;
    border-radius: 24px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    padding: 2rem 1.25rem;

    @media (min-width: $breakpoint-md) {
        border-radius: 32px;
        padding: 3rem 4rem;
    }
}

.tow-col__heading {
    margin: 0 0 2rem;
    text-align: center;
    font-size: 1.75rem;
    font-weight: 800;
    color: $color-text;
    letter-spacing: -0.01em;

    @media (min-width: $breakpoint-md) {
        font-size: 2.5rem;
        margin-bottom: 3rem;
    }
}

.tow-col__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;

    @media (min-width: $breakpoint-md) {
        gap: 2rem;
    }
}

.tow-col__column {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;

    @media (min-width: $breakpoint-md) {
        gap: 2.5rem;
    }
}

.tow-col__link {
    font-size: 0.6rem;
    color: $color-text;
    cursor: pointer;
    text-align: center;
    transition: color 0.15s ease;
    text-decoration: none;
    line-height: 1.4;

    @media (min-width: $breakpoint-md) {
        font-size: 1rem;
    }

    &:hover {
        color: $link-color;
    }
}
</style>
